import { clampBpm, clampGenre, clampMasterVolume, clampUnit } from "../utils/validation";
import { createDefaultControls } from "./defaultControls";
import type { ParameterPath } from "./parameterRoutes";
import type { UserControls } from "./types";

export type ControlValue = number | boolean | string;

/**
 * The only holder of user control values.
 *
 * - React reads it through useSyncExternalStore (snapshot identity changes on
 *   every write, so components re-render).
 * - The audio side reads the current snapshot directly; immediate/audioRate
 *   parameters are pushed by AudioParameterController subscribing here, while
 *   musical parameters are pulled at bar boundaries.
 *
 * Safety ceilings are enforced here so no downstream path can exceed them.
 */
export class ControlStore {
  private state: UserControls;
  private readonly listeners = new Set<() => void>();

  constructor(initial: UserControls = createDefaultControls()) {
    this.state = initial;
  }

  getSnapshot = (): UserControls => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  set(path: ParameterPath, value: ControlValue): void {
    const next = this.withValue(this.state, path, value);
    if (next === this.state) return;
    this.state = next;
    for (const listener of this.listeners) listener();
  }

  private withValue(state: UserControls, path: ParameterPath, value: ControlValue): UserControls {
    switch (path) {
      case "transport.bpmManual": {
        const bpm = clampBpm(Number(value));
        if (bpm === state.transport.bpmManual) return state;
        return { ...state, transport: { ...state.transport, bpmManual: bpm } };
      }
      case "transport.autoTempo": {
        const autoTempo = Boolean(value);
        if (autoTempo === state.transport.autoTempo) return state;
        return { ...state, transport: { ...state.transport, autoTempo } };
      }
      case "sound.masterVolume": {
        const volume = clampMasterVolume(Number(value));
        if (volume === state.sound.masterVolume) return state;
        return { ...state, sound: { ...state.sound, masterVolume: volume } };
      }
      case "sound.muted": {
        const muted = Boolean(value);
        if (muted === state.sound.muted) return state;
        return { ...state, sound: { ...state.sound, muted } };
      }
      case "genre.ambient":
      case "genre.techno":
      case "genre.jazz": {
        const key = path.split(".")[1] as "ambient" | "techno" | "jazz";
        const next = clampGenre(Number(value));
        if (next === state.genre[key]) return state;
        return { ...state, genre: { ...state.genre, [key]: next } };
      }
      case "main.energy":
      case "main.density": {
        const key = path.split(".")[1] as "energy" | "density";
        const next = clampUnit(Number(value));
        if (next === state.main[key]) return state;
        return { ...state, main: { ...state.main, [key]: next } };
      }
      case "diagnosticClick": {
        const enabled = Boolean(value);
        if (enabled === state.diagnosticClick) return state;
        return { ...state, diagnosticClick: enabled };
      }
      case "seed": {
        const seed = String(value);
        if (seed === state.seed) return state;
        return { ...state, seed };
      }
    }
  }
}
